import { Component } from '@theme/component';
import { ThemeEvents, MediaStartedPlayingEvent } from '@theme/events';
import { DialogCloseEvent } from '@theme/dialog';
import { prefersReducedMotion } from '@theme/utilities';

/**
 * A deferred media element
 * @typedef {Object} Refs
 * @property {HTMLElement} deferredMediaPlayButton - The button to show the deferred media content
 * @property {HTMLElement} toggleMediaButton - The button to toggle the media
 *
 * @extends {Component<Refs>}
 */
class DeferredMedia extends Component {
  /** @type {boolean} */
  isPlaying = false;

  #abortController = new AbortController();

  connectedCallback() {
    super.connectedCallback();
    const signal = this.#abortController.signal;
    // If we're to use deferred media for images, we will need to run this only when it's not an image type media
    document.addEventListener(ThemeEvents.mediaStartedPlaying, this.pauseMedia.bind(this), { signal });
    window.addEventListener(DialogCloseEvent.eventName, this.pauseMedia.bind(this), { signal });
    this.#syncAutoplayState();

    // Reduced motion can be switched on after load; re-evaluate rather than
    // reading the preference once at connect (WCAG 2.2.2).
    window
      .matchMedia('(prefers-reduced-motion: reduce)')
      .addEventListener('change', this.#onReducedMotionChange, { signal });
  }

  /**
   * @param {MediaQueryListEvent} event
   */
  #onReducedMotionChange = (event) => {
    // Only when the preference turns ON, and only for media that started on its own
    // (WCAG 2.2.2 is about auto-playing content). A video the user pressed play on
    // is theirs to stop.
    if (!event.matches || !this.hasAttribute('autoplay')) return;

    this.#stripIframeAutoplay();
    this.#stripVideoAutoplay();
    this.pauseMedia();
  };

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#abortController.abort();
  }

  /**
   * Updates the visual hint for play/pause state
   * @param {boolean} isPlaying - Whether the video is currently playing
   */
  updatePlayPauseHint(isPlaying) {
    const toggleMediaButton = this.refs.toggleMediaButton;
    if (toggleMediaButton instanceof HTMLElement) {
      toggleMediaButton.classList.remove('hidden');
      const playIcon = toggleMediaButton.querySelector('.icon-play');
      if (playIcon) playIcon.classList.toggle('hidden', isPlaying);
      const pauseIcon = toggleMediaButton.querySelector('.icon-pause');
      if (pauseIcon) pauseIcon.classList.toggle('hidden', !isPlaying);

      const playLabel = toggleMediaButton.dataset.labelPlay;
      const pauseLabel = toggleMediaButton.dataset.labelPause;
      const label = isPlaying ? pauseLabel : playLabel;
      if (label) toggleMediaButton.setAttribute('aria-label', label);
    }
  }

  /**
   * Sync play/pause UI for videos that already autoplay in the live DOM (WCAG 2.2.2).
   */
  #syncAutoplayState() {
    const video = this.querySelector(':scope > video');
    const hasAutoplay = this.hasAttribute('autoplay') || video?.hasAttribute('autoplay');

    if (!hasAutoplay) return;

    if (prefersReducedMotion()) {
      this.#stripIframeAutoplay();
      this.#stripVideoAutoplay();
      this.pauseMedia();
      return;
    }

    this.isPlaying = true;
    this.updatePlayPauseHint(true);
  }

  /**
   * Drops the `autoplay` attribute from a live <video> so a re-insert (a morph, a
   * gallery re-render) cannot restart it behind the user's back. pause() alone only
   * affects the current element instance.
   */
  #stripVideoAutoplay() {
    for (const video of this.querySelectorAll('video[autoplay]')) {
      video.removeAttribute('autoplay');
    }
  }

  /**
   * Takes autoplay out of a live iframe embed's URL so it loads paused.
   * A postMessage pause can be dropped when it lands before the player's JS API is
   * ready — which is exactly the case at connectedCallback — so for reduced motion
   * we rewrite the src instead. Works for every provider, including ones with no
   * postMessage API (Wistia).
   */
  #stripIframeAutoplay() {
    const iframe = this.querySelector('iframe');
    if (!(iframe instanceof HTMLIFrameElement) || !iframe.src) return;

    try {
      const url = new URL(iframe.src, window.location.origin);
      if (!url.searchParams.has('autoplay')) return;
      url.searchParams.delete('autoplay');
      iframe.src = url.toString();
    } catch {
      // Leave the src alone if it is not a valid absolute URL.
    }
  }

  /**
   * Shows the deferred media content
   */
  showDeferredMedia = () => {
    this.loadContent(true);
    this.playMedia();
  };

  /**
   * Loads the content
   * @param {boolean} [focus] - Whether to focus the content
   */
  loadContent(focus = true) {
    if (this.getAttribute('data-media-loaded')) return;

    this.dispatchEvent(new MediaStartedPlayingEvent(this));

    const content = this.querySelector('template')?.content.firstElementChild?.cloneNode(true);

    if (!content) return;

    // Deferred external embeds: Liquid omits autoplay from <template> (WCAG 2.2.2).
    // Add it only when the iframe enters the live DOM after a poster click.
    if (content instanceof HTMLIFrameElement && content.src) {
      try {
        const url = new URL(content.src, window.location.origin);
        if (!url.searchParams.has('autoplay')) {
          const isWistia =
            content.classList.contains('js-wistia') || url.hostname.includes('wistia');
          url.searchParams.set('autoplay', isWistia ? 'true' : '1');
          content.src = url.toString();
        }
      } catch {
        // Keep the cloned src if it is not a valid absolute URL.
      }
    }

    this.setAttribute('data-media-loaded', 'true');
    this.appendChild(content);

    if (focus && content instanceof HTMLElement) {
      content.focus();
    }

    this.refs.deferredMediaPlayButton?.classList.add('deferred-media__playing');

    if (content instanceof HTMLVideoElement && content.getAttribute('autoplay')) {
      // force autoplay for safari
      content.play();
    }
  }

  /**
   * Toggle play/pause state of the media
   */
  toggleMedia() {
    if (this.isPlaying) {
      this.pauseMedia();
    } else {
      this.playMedia();
    }
  }

  playMedia() {
    /** @type {HTMLIFrameElement | null} */
    const iframe = this.querySelector('iframe[data-video-type]');
    if (iframe) {
      iframe.contentWindow?.postMessage(
        iframe.dataset.videoType === 'youtube'
          ? '{"event":"command","func":"playVideo","args":""}'
          : '{"method":"play"}',
        '*'
      );
    } else {
      this.querySelector('video')?.play();
    }
    this.isPlaying = true;
    this.updatePlayPauseHint(this.isPlaying);
  }

  /**
   * Pauses the media
   */
  pauseMedia() {
    /** @type {HTMLIFrameElement | null} */
    const iframe = this.querySelector('iframe[data-video-type]');

    if (iframe) {
      iframe.contentWindow?.postMessage(
        iframe.dataset.videoType === 'youtube'
          ? '{"event":"command","func":"' + 'pauseVideo' + '","args":""}'
          : '{"method":"pause"}',
        '*'
      );
    } else {
      this.querySelector('video')?.pause();
    }
    this.isPlaying = false;

    // If we've already revealed the deferred media, we should toggle the play/pause hint
    if (this.getAttribute('data-media-loaded')) {
      this.updatePlayPauseHint(this.isPlaying);
    }
  }
}

if (!customElements.get('deferred-media')) {
  customElements.define('deferred-media', DeferredMedia);
}

/**
 * A product model
 */
class ProductModel extends DeferredMedia {
  #abortController = new AbortController();

  loadContent() {
    super.loadContent();

    Shopify.loadFeatures([
      {
        name: 'model-viewer-ui',
        version: '1.0',
        onLoad: this.setupModelViewerUI.bind(this),
      },
    ]);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#abortController.abort();
  }

  pauseMedia() {
    super.pauseMedia();
    this.modelViewerUI?.pause();
  }

  playMedia() {
    super.playMedia();
    this.modelViewerUI?.play();
  }

  /**
   * @param {Error[]} errors
   */
  async setupModelViewerUI(errors) {
    if (errors) return;

    if (!Shopify.ModelViewerUI) {
      await this.#waitForModelViewerUI();
    }

    if (!Shopify.ModelViewerUI) return;

    const element = this.querySelector('model-viewer');
    if (!element) return;

    const signal = this.#abortController.signal;

    this.modelViewerUI = new Shopify.ModelViewerUI(element);
    if (!this.modelViewerUI) return;

    this.playMedia();

    // Track pointer events to detect taps
    let pointerStartX = 0;
    let pointerStartY = 0;

    element.addEventListener(
      'pointerdown',
      (/** @type {PointerEvent} */ event) => {
        pointerStartX = event.clientX;
        pointerStartY = event.clientY;
      },
      { signal }
    );

    element.addEventListener(
      'click',
      (/** @type {PointerEvent} */ event) => {
        const distanceX = Math.abs(event.clientX - pointerStartX);
        const distanceY = Math.abs(event.clientY - pointerStartY);
        const totalDistance = Math.sqrt(distanceX * distanceX + distanceY * distanceY);

        // Try to ensure that this is a tap, not a drag.
        if (totalDistance < 10) {
          // When the model is paused, it has its own button overlay for playing the model again.
          // If we're receiving a click event, it means the model is playing, all we can do is pause it.
          this.pauseMedia();
        }
      },
      { signal }
    );
  }

  /**
   * Waits for Shopify.ModelViewerUI to be defined.
   * This seems to be necessary for Safari since Shopify.ModelViewerUI is always undefined on the first try.
   * @returns {Promise<void>}
   */
  async #waitForModelViewerUI() {
    const maxAttempts = 10;
    const interval = 50;

    for (let i = 0; i < maxAttempts; i++) {
      if (Shopify.ModelViewerUI) {
        return;
      }
      await new Promise((resolve) => setTimeout(resolve, interval));
    }
  }
}

if (!customElements.get('product-model')) {
  customElements.define('product-model', ProductModel);
}
